// frontend/src/pages/LoginPage.tsx - NEW FUNCTION - Fixed version with error prevention
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/auth';
import { useAppStore } from '@/store/app';

// UI Components - Updated imports
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorAlert } from '@/components/ui/ErrorAlert';

// Utils
import { cn } from '@/utils';

// Types
interface LoginFormData {
  username: string;
  organization: string;
  password: string;
}

interface LoginFormErrors {
  username?: string;
  organization?: string;
  password?: string;
  general?: string;
}

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Store hooks - Updated to match actual store methods
  const {
    login,
    isLoading: authLoading,
    error: authError,
    clearError: clearAuthError,
    isAuthenticated,
    checkAuthQuick,
    getSafeUser,
    validateCurrentUser
  } = useAuthStore();

  const {
    clearGlobalError
  } = useAppStore();

  // Extract organization from URL query parameter
  const searchParams = new URLSearchParams(location.search);
  const orgFromUrl = searchParams.get('org') || searchParams.get('organization') || '';

  // Form state
  const [formData, setFormData] = useState<LoginFormData>({
    username: '',
    organization: orgFromUrl,
    password: ''
  });

  const [formErrors, setFormErrors] = useState<LoginFormErrors>({});
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // Check if already authenticated and redirect
  useEffect(() => {
    
    try {
      // Use the enhanced auth validation methods safely
      const isQuickAuthValid = checkAuthQuick?.();
      const user = getSafeUser?.();
      const isUserValid = validateCurrentUser?.();
      

      // If authenticated and have valid user, redirect to intended page
      if (isAuthenticated && user && (isQuickAuthValid || isUserValid)) {
        const from = location.state?.from?.pathname || '/dashboard';
        navigate(from, { replace: true });
      }
    } catch (error) {
      console.warn('Auth check failed:', error);
      // Continue to show login form
    }
  }, [isAuthenticated, navigate, location.state, checkAuthQuick, getSafeUser, validateCurrentUser]);

  // Clear errors when form data changes
  useEffect(() => {
    if (Object.keys(formErrors).length > 0) {
      setFormErrors({});
    }
  }, [formData]);

  // Handle input changes with proper typing
  const handleInputChange = (field: keyof LoginFormData) => 
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setFormData(prev => ({ ...prev, [field]: value }));
      
      // Clear field-specific errors
      if (formErrors[field]) {
        setFormErrors(prev => ({ ...prev, [field]: undefined }));
      }
    };

  // Form validation
  const validateForm = (): boolean => {
    const errors: LoginFormErrors = {};

    if (!formData.username.trim()) {
      errors.username = 'Username is required';
    }

    // Organization is required but can come from URL
    if (!formData.organization.trim() && !orgFromUrl) {
      errors.organization = 'Organization is required';
    }

    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 3) {
      errors.password = 'Password must be at least 3 characters';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Clear any global errors
    if (clearGlobalError) clearGlobalError();
    if (clearAuthError) clearAuthError();
    
    // Validate form
    if (!validateForm()) {
      return;
    }

    try {
      // Use organization from URL if available, otherwise use form data
      const organizationToUse = orgFromUrl || formData.organization.trim();


      // Updated to match actual auth store login method signature
      await login(
        formData.username.trim(),
        organizationToUse,
        formData.password
      );


      // Success - navigation will be handled by useEffect

    } catch (error) {
      // Error handling is managed by the auth store
      console.error('Login failed:', error);
      
      // Enhanced error logging to debug API response
      if (error instanceof Error) {
        console.error('Error details:', {
          message: error.message,
          stack: error.stack
        });
        
        // Optionally set additional error context if needed
        setFormErrors({ general: error.message });
      }
    }
  };

  // Handle demo login (if available in development)
  const handleDemoLogin = async () => {
    setFormData({
      username: 'demo',
      organization: orgFromUrl || 'demo',
      password: 'demo123'
    });

    // Auto-submit demo form after a brief delay
    setTimeout(() => {
      const form = document.querySelector('form');
      if (form) {
        const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
        form.dispatchEvent(submitEvent);
      }
    }, 100);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header with WorkiDoc Branding */}
        <div className="text-center">
          <div className="flex flex-col items-center">
            <h1 className="text-5xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              WorkiDoc
            </h1>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 font-medium">
              Intelligent Document Management
            </p>
          </div>
          <h2 className="mt-8 text-2xl font-semibold text-gray-900 dark:text-white">
            Sign in to your account
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Access your email management dashboard
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-white dark:bg-gray-800 py-8 px-6 shadow-lg rounded-lg border border-gray-200 dark:border-gray-700">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Global Error */}
            {authError && (
              <ErrorAlert 
                message={authError}
                onClose={clearAuthError || (() => {})}
                className="mb-4"
              />
            )}

            {/* Form-specific errors */}
            {formErrors.general && (
              <ErrorAlert 
                message={formErrors.general}
                onClose={() => setFormErrors(prev => ({ ...prev, general: undefined }))}
                className="mb-4"
              />
            )}

            {/* Username Field */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Username
              </label>
              <Input
                id="username"
                name="username"
                type="text"
                autoComplete="username"
                required
                value={formData.username}
                onChange={handleInputChange('username')}
                error={formErrors.username}
                placeholder="Enter your username"
                disabled={authLoading}
                className="w-full"
              />
            </div>

            {/* Organization Field - Only show if not provided in URL */}
            {!orgFromUrl && (
              <div>
                <label htmlFor="organization" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Organization
                </label>
                <Input
                  id="organization"
                  name="organization"
                  type="text"
                  autoComplete="organization"
                  required
                  value={formData.organization}
                  onChange={handleInputChange('organization')}
                  error={formErrors.organization}
                  placeholder="Enter your organization"
                  disabled={authLoading}
                  className="w-full"
                />
              </div>
            )}

            {/* Show organization name when it comes from URL */}
            {orgFromUrl && (
              <div className="text-center py-2 px-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Signing in to{' '}
                  <span className="font-semibold text-blue-600 dark:text-blue-400">
                    {orgFromUrl}
                  </span>
                </p>
              </div>
            )}

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Password
              </label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleInputChange('password')}
                  error={formErrors.password}
                  placeholder="Enter your password"
                  disabled={authLoading}
                  className="w-full pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  disabled={authLoading}
                >
                  <svg 
                    className={cn(
                      "h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300",
                      authLoading && "opacity-50"
                    )} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    {showPassword ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L8.464 8.464M9.878 9.878l4.242 4.242m0 0L15.536 15.536M14.12 14.12l4.242 4.242" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    )}
                  </svg>
                </button>
              </div>
            </div>

            {/* Remember me checkbox */}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  disabled={authLoading}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                  Remember me
                </label>
              </div>

              <div className="text-sm">
                <a href="#" className="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400">
                  Forgot password?
                </a>
              </div>
            </div>

            {/* Submit button */}
            <div>
              <Button
                type="submit"
                disabled={authLoading}
                className="w-full flex justify-center py-3"
                size="lg"
              >
                {authLoading ? (
                  <LoadingSpinner size="sm" color="white" className="mr-2" />
                ) : null}
                {authLoading ? 'Signing in...' : 'Sign in'}
              </Button>
            </div>

            {/* Demo login button for development */}
            {process.env.NODE_ENV === 'development' && (
              <div className="mt-4">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleDemoLogin}
                  disabled={authLoading}
                  className="w-full"
                  size="md"
                >
                  Demo Login
                </Button>
              </div>
            )}
          </form>
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Need help?{' '}
            <a href="#" className="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400">
              Contact your administrator
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};